import { useGetCases } from '@/store/api/services/operations/cases';
import type { Case } from '@/types';
import { Autocomplete, TextField } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';
import { useState } from 'react';
import { type Control, Controller, type FieldErrors } from 'react-hook-form';

import type { ServiceTicketCreateSchemaType } from '../schema';

interface CaseSelectProps {
  control: Control<ServiceTicketCreateSchemaType>;
  errors: FieldErrors<ServiceTicketCreateSchemaType>;
  label?: string;
  helperText?: string;
}

/** Search-as-you-type case picker, shared by every place a service ticket form can attach a case. */
export const CaseSelect = ({
  control,
  errors,
  label = 'Related Case (Optional)',
  helperText,
}: CaseSelectProps) => {
  const [caseSearchQuery, setCaseSearchQuery] = useState('');
  const [openCasesDropdown, setOpenCasesDropdown] = useState(false);

  const { data: casesData, isFetching: isLoadingCases } = useGetCases(
    openCasesDropdown
      ? {
          pagination: {
            current_page: 1,
            items_per_page: 25,
            sort_by: 'created_at',
            sort_ascending: false,
          },
          search_text: caseSearchQuery,
        }
      : skipToken,
  );

  const cases = casesData?.data || [];

  return (
    <Controller
      name="case_id"
      control={control}
      render={({ field }) => {
        const selectedCase = cases.find((c) => c.id === field.value) || null;
        return (
          <Autocomplete<Case>
            disablePortal
            open={openCasesDropdown}
            onOpen={() => setOpenCasesDropdown(true)}
            onClose={() => setOpenCasesDropdown(false)}
            options={cases}
            loading={isLoadingCases}
            value={selectedCase}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              const caseLabel = option.display_id
                ? `Case #${option.display_id}`
                : `Case ${option.id}`;
              return `${caseLabel} - ATM ${option.atm_id}`;
            }}
            isOptionEqualToValue={(option, value) => option.id === (value?.id ?? '')}
            filterOptions={(options) => options.filter((option) => option.id !== field.value)}
            onChange={(_, value) => {
              field.onChange(value?.id || '');
            }}
            onInputChange={(_, value) => {
              setCaseSearchQuery(value);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                size="small"
                fullWidth
                placeholder="Search case by ID or ATM"
                error={!!errors.case_id}
                helperText={helperText || errors.case_id?.message}
                InputProps={{
                  ...params.InputProps,
                }}
                data-test="service-ticket-case-select"
              />
            )}
          />
        );
      }}
    />
  );
};
